import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { API_BASE } from "../lib/api";

/**
 * MaintenanceBanner
 * -----------------
 * Polls `/api/system/status` on mount + every 60s. Renders a full-width
 * amber banner above the app when `maintenance_mode` is true.
 *
 * Dismissal is intentionally in-memory only — the banner reappears on hard
 * refresh so the message never gets buried across sessions.
 */

export default function MaintenanceBanner() {
  const [maintenance, setMaintenance] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/system/status`, { credentials: "omit" });
        if (!res.ok) return;
        const data = await res.json();
        setMaintenance(!!data.maintenance_mode);
      } catch { /* ignore */ }
    };

    poll();
    const t = setInterval(poll, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!maintenance || dismissed) return null;

  return (
    <div
      role="status"
      className="w-full bg-amber-500 text-navy border-b border-amber-600/40"
      data-testid="maintenance-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <AlertTriangle size={16} className="shrink-0" />
        <div className="text-sm font-heading flex-1 min-w-0">
          <span className="font-semibold">Scheduled maintenance in progress.</span>{" "}
          <span className="text-navy/85">Parts of VenturaZ may be temporarily unavailable. Signups and sign-ins are still open.</span>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss maintenance banner"
          data-testid="maintenance-banner-dismiss"
          className="p-1 rounded hover:bg-navy/10 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
